import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Stepper,
  Step,
  StepLabel,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CognitoAiFileUploadPickerPane from "./CognitoAiFileUploadPickerPane";
import CognitoAiFileUploadAdditionalInfoPane from "./CognitoAiFileUploadAdditionalInfoPane";
import CognitoAiFileUploadProgressPane from "./CognitoAiFileUploadProgressPane";
import CognitoAiFileUploadCompletePane from "./CognitoAiFileUploadCompletePane";
import CognitoAiFileUploadFailedPane from "./CognitoAiFileUploadFailedPane";
import { getCognitoAiVariants } from "../../services/CognitoAi";
import Constants from "../../services/Constants";

const STEPS = ["Select File", "File Details", "Upload"];

const CognitoAiFileUpload = ({ open, handleClose, onUploadSuccess }) => {
  const { UPLOAD_STATUS } = Constants;
  const [step, setStep] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileType, setFileType] = useState("");
  const [fileSize, setFileSize] = useState(0);
  const [uploadMetadata, setUploadMetadata] = useState(null);
  const [stsToken, setStsToken] = useState(null);
  const [cognitoAiBucket, setCognitoAiBucket] = useState("");
  const [variants, setVariants] = useState([]);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [uploadProgress, setUploadProgress] = useState({
    loaded: 0,
    total: 100,
  });
  const [uploadError, setUploadError] = useState(null);
  const [uploadInstance, setUploadInstance] = useState(null);

  useEffect(() => {
    if (open) {
      fetchVariants();
    }
  }, [open]);

  const fetchVariants = async () => {
    try {
      const variantList = await getCognitoAiVariants();
      setVariants(variantList || []);
    } catch (err) {
      console.error("Error fetching variants:", err);
    }
  };

  const handleFileSelected = (file) => {
    setSelectedFile(file);
    setFileType(file.type || getFileType(file.name));
    setFileSize(file.size);
    setStep(1);
  };

  const getFileType = (fileName) => {
    const name = fileName.toLowerCase();
    if (name.endsWith(".tar.gz") || name.endsWith(".tgz")) {
      return "application/gzip";
    }
    if (name.endsWith(".gz")) return "application/gzip";
    if (name.endsWith(".tar")) return "application/x-tar";
    if (name.endsWith(".iso")) return "application/x-iso9660-image";
    return "application/octet-stream";
  };

  const handleAdditionalInfoComplete = (data) => {
    setUploadMetadata(data.uploadMetadata);
    setStsToken(data.stsToken);
    setCognitoAiBucket(data.cognitoAiBucket);
    setStep(2);
    setUploadStatus(UPLOAD_STATUS.Started);
  };

  const handleUploadComplete = (uploadData) => {
    setUploadStatus(UPLOAD_STATUS.Completed);
  };

  const handleUploadFailed = (error) => {
    setUploadStatus(UPLOAD_STATUS.Failed);
    setUploadError(error);
  };

  const handleUploadProgress = (progress) => {
    setUploadProgress(progress);
  };

  const onClose = useCallback(() => {
    setStep(0);
    setSelectedFile(null);
    setFileType("");
    setFileSize(0);
    setUploadMetadata(null);
    setStsToken(null);
    setCognitoAiBucket("");
    setUploadStatus(null);
    setUploadProgress({ loaded: 0, total: 100 });
    setUploadError(null);
    setUploadInstance(null);
  }, []);

  const popUpClose = () => {
    handleClose();
    onClose();
    if (uploadStatus === UPLOAD_STATUS.Completed && onUploadSuccess) {
      onUploadSuccess();
    }
  };

  const handleDialogClose = () => {
    if (
      uploadStatus === UPLOAD_STATUS.InProgress ||
      uploadStatus === UPLOAD_STATUS.Started
    ) {
      // Show confirmation dialog
      if (
        window.confirm(
          "Upload is in progress. Are you sure you want to cancel?",
        )
      ) {
        popUpClose();
      }
    } else {
      popUpClose();
    }
  };

  const renderStepContent = () => {
    switch (step) {
      case 0:
        return (
          <CognitoAiFileUploadPickerPane onFileSelected={handleFileSelected} />
        );
      case 1:
        return (
          <CognitoAiFileUploadAdditionalInfoPane
            fileName={selectedFile?.name}
            fileType={fileType}
            fileSize={fileSize}
            file={selectedFile}
            variants={variants}
            onComplete={handleAdditionalInfoComplete}
            onBack={() => setStep(0)}
          />
        );
      case 2:
        if (uploadStatus === UPLOAD_STATUS.Completed) {
          return (
            <CognitoAiFileUploadCompletePane
              fileName={selectedFile?.name}
              uploadId={uploadMetadata?.uploadId}
              onClose={popUpClose}
            />
          );
        } else if (uploadStatus === UPLOAD_STATUS.Failed) {
          return (
            <CognitoAiFileUploadFailedPane
              error={uploadError}
              onRetry={() => {
                setUploadStatus(UPLOAD_STATUS.Started);
                setUploadError(null);
              }}
              onClose={popUpClose}
            />
          );
        } else {
          return (
            <CognitoAiFileUploadProgressPane
              file={selectedFile}
              uploadMetadata={uploadMetadata}
              stsToken={stsToken}
              cognitoAiBucket={cognitoAiBucket}
              onComplete={handleUploadComplete}
              onFailed={handleUploadFailed}
              uploadProgress={uploadProgress}
              setUploadProgress={setUploadProgress}
              setUploadStatus={setUploadStatus}
              uploadInstance={uploadInstance}
              setUploadInstance={setUploadInstance}
            />
          );
        }
      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Cognito AI Image Upload
        <IconButton
          aria-label="close"
          onClick={handleDialogClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Stepper activeStep={step}>
            {STEPS.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>
        {renderStepContent()}
      </DialogContent>
    </Dialog>
  );
};

export default CognitoAiFileUpload;
